import React, { SetStateAction, useState } from "react";
import { TableCellsIcon, CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import "./DAPTable.css";
import ApiService from "../../services/api";
import ToastMessage from "../ToastMessage/ToastMessage";

export interface DAPTableColumn {
  key: any;
  label: string;
  width?: string;
  align?: "left" | "center" | "right";
  render?: (
    value: any,
    row: any,
    onChange: (val: any) => void
  ) => React.ReactNode;
}

export interface DAPTableRow {
  [key: string]: any;
}

export interface DAPTableProps {
  columns: DAPTableColumn[];
  data: DAPTableRow[];
  title?: string;
  showTitle?: boolean;
  className?: string;
  emptyMessage?: string;
  striped?: boolean;
  hoverable?: boolean;
  fileId?: number;
  showSaveButton?: boolean;
  showValidateButton?: boolean;
  setShowToast?: (data: boolean) => void;
  setToastType?: (data: SetStateAction<"success" | "error" | "info">) => void;
  onMetadataSaved?: (newMetadataFile: { id: number; name: string; path: string }) => void;
  onValidationStatusChange?: (isValidationPassed: boolean) => void;
  onDataChange?: (updatedData: DAPTableRow[]) => void;
}

const DAPTable: React.FC<DAPTableProps> = ({
  columns,
  data,
  title = "Data Table",
  showTitle = true,
  className = "",
  emptyMessage = "No data available.",
  striped = false,
  hoverable = true,
  fileId = 0,
  showSaveButton = true,
  showValidateButton = true,
  setShowToast = () => {},
  setToastType = () => {},
  onMetadataSaved = () => {},
  onValidationStatusChange = () => {},
  onDataChange = () => {},
}) => {
  const [tableData, setTableData] = useState<DAPTableRow[]>(data);
  const [editingCell, setEditingCell] = useState<{
    rowIndex: number;
    colKey: string;
  } | null>(null);
  const [validationResults, setValidationResults] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showValidationResults, setShowValidationResults] = useState(false);
  const [isValidationPassed, setIsValidationPassed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDetailedResultsCollapsed, setIsDetailedResultsCollapsed] = useState(true);

  const handleCellChange = (rowIndex: number, key: string, value: any) => {
    setTableData((prev) => {
      const updated = [...prev];
      updated[rowIndex] = { ...updated[rowIndex], [key]: value };
      onDataChange(updated);
      return updated;
    });
    
    // Reset validation state when user edits after validation
    if (isValidationPassed) {
      setIsValidationPassed(false);
      setShowValidationResults(false);
      setValidationResults(null);
      onValidationStatusChange(false);
    }
  };

  const handleValidateChanges = async () => {
    setIsValidating(true);
    setValidationResults(null);
    setShowValidationResults(false);
    setIsValidationPassed(false);

    try {
      // Prepare metadata for validation
      const metadata = tableData.map((row) => {
        const exportedRow: Record<string, any> = {};
        columns.forEach((col) => {
          exportedRow[col.key] = row[col.key] ?? "";
        });
        return exportedRow;
      });

      // Validate metadata content only
      console.log("Validating metadata content...");
      const validationResult = await ApiService.validateMetadataContent(metadata);
      setValidationResults(validationResult);
      setShowValidationResults(true);
      setIsValidating(false);

      // Check validation status
      if (validationResult.overall_status === "PASS") {
        setIsValidationPassed(true);
        onValidationStatusChange(true);
        console.log("Validation passed - Save button will be enabled");
      } else {
        setIsValidationPassed(false);
        onValidationStatusChange(false);
        console.log("Validation failed - Save button will remain disabled");
      }
    } catch (error) {
      console.error("Error during validation:", error);
      setIsValidating(false);
      setIsValidationPassed(false);
      setShowToast(true);
      setToastType("error");
    }
  };

  const handleSaveMetadata = async () => {
    if (!isValidationPassed) {
      console.log("Cannot save - validation has not passed");
      return;
    }

    setIsSaving(true);

    try {
      // Prepare metadata for saving
      const metadata = tableData.map((row) => {
        const exportedRow: Record<string, any> = {};
        columns.forEach((col) => {
          exportedRow[col.key] = row[col.key] ?? "";
        });
        return exportedRow;
      });

      console.log("Saving metadata to database...");
      const metaPayload = {
        metadata_file_id: fileId,
        full_metadata: metadata,
      };
      
      const res = await ApiService.editMetaDataTable(metaPayload);
      console.log('API Response:', res);
      
      // Check if the response indicates success
      const isSuccess = res?.message && (
        res.message.includes("updated same DB row") ||
        res.message.includes("updated it instead of inserting") ||
        res.message.includes("created a single _edit row") ||
        res.message === "Metadata file edited and saved successfully"
      );
      
      if (isSuccess) {
        setShowToast(true);
        setToastType("success");
        
        // Notify parent component about the new metadata file
        if (res.metadata_file_id && res.file_name && res.file_path) {
          onMetadataSaved({
            id: res.metadata_file_id,
            name: res.file_name,
            path: res.file_path
          });
        }
      } else {
        setShowToast(true);
        setToastType("error");
      }
    } catch (error) {
      console.error("Error during save:", error);
      setShowToast(true);
      setToastType("error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={`card p-6 ${className}`}>
      {showTitle && (
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-secondary-900 flex items-center">
            <TableCellsIcon className="w-6 h-6 text-primary-600 mr-3" />
            {title}
          </h2>
        </div>
      )}

      <div className="overflow-auto scroll-container max-h-[600px]">
        <table className="w-full table-auto border-collapse">
          <thead>
            <tr className="bg-secondary-50">
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={`px-4 py-3 text-${
                    column.align || "left"
                  } text-sm font-medium text-secondary-700 border-b`}
                  style={{ width: column.width }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.length > 0 ? (
              tableData.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={`${
                    striped && rowIndex % 2 === 1 ? "bg-secondary-25" : ""
                  } ${hoverable ? "hover:bg-secondary-50" : ""}`}
                >
                  {columns.map((column, colIndex) => {
                    const isEditing =
                      editingCell?.rowIndex === rowIndex &&
                      editingCell?.colKey === column.key;

                    return (
                      <td
                        key={colIndex}
                        className={`px-4 py-3 text-sm text-secondary-900 border-b text-${
                          column.align || "left"
                        }`}
                        style={{
                          maxWidth: "350px",
                          minWidth: "200px",
                          overflow: isEditing ? "visible" : "hidden",
                          textOverflow: isEditing ? "clip" : "ellipsis",
                          whiteSpace: isEditing ? "normal" : "nowrap",
                          wordWrap: isEditing ? "break-word" : "normal",
                          overflowWrap: isEditing ? "break-word" : "normal",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          !isEditing &&
                          setEditingCell({ rowIndex, colKey: column.key })
                        }
                        title={row[column.key]}
                      >
                        {isEditing ? (
                          <input
                            type="text"
                            value={row[column.key] ?? ""}
                            onChange={(e) =>
                              handleCellChange(
                                rowIndex,
                                column.key,
                                e.target.value
                              )
                            }
                            onBlur={() => setEditingCell(null)}
                            className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                            style={{
                              minWidth: "200px",
                              maxWidth: "350px",
                            }}
                            autoFocus
                          />
                        ) : typeof row[column.key] === "boolean" ? (
                          JSON.stringify(row[column.key])
                        ) : (
                          row[column.key] ?? "--"
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-secondary-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Validation Results Section */}
      {showValidationResults && validationResults && (
        <div className="mt-6 p-4 border rounded-lg bg-gray-50">
          <div className="flex items-center mb-3">
            {validationResults.overall_status === "PASS" ? (
              <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
            ) : validationResults.overall_status === "FAIL" ? (
              <XCircleIcon className="w-5 h-5 text-red-600 mr-2" />
            ) : (
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mr-2" />
            )}
            <h3 className="text-lg font-semibold">
              Validation Results - {validationResults.overall_status}
            </h3>
          </div>
          
          <div className="grid grid-cols-4 gap-4 mb-4 text-sm">
            <div className="bg-white p-3 rounded border">
              <div className="font-medium text-gray-700">Total Rules</div>
              <div className="text-xl font-bold text-gray-900">
                {validationResults.summary.total_rules}
              </div>
            </div>
            <div className="bg-white p-3 rounded border">
              <div className="font-medium text-green-700">Passed</div>
              <div className="text-xl font-bold text-green-600">
                {validationResults.summary.passed_rules}
              </div>
            </div>
            <div className="bg-white p-3 rounded border">
              <div className="font-medium text-red-700">Failed</div>
              <div className="text-xl font-bold text-red-600">
                {validationResults.summary.failed_rules}
              </div>
            </div>
            <div className="bg-white p-3 rounded border">
              <div className="font-medium text-yellow-700">Warnings</div>
              <div className="text-xl font-bold text-yellow-600">
                {validationResults.summary.warning_rules}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900">Detailed Results:</h4>
              <button
                onClick={() => setIsDetailedResultsCollapsed(!isDetailedResultsCollapsed)}
                className="text-gray-600 hover:text-gray-800 transition-colors p-1 rounded hover:bg-gray-100"
                title={isDetailedResultsCollapsed ? "Expand" : "Collapse"}
              >
                {isDetailedResultsCollapsed ? (
                  <ChevronDownIcon className="w-4 h-4" />
                ) : (
                  <ChevronUpIcon className="w-4 h-4" />
                )}
              </button>
            </div>
            
            {!isDetailedResultsCollapsed && (
              <div className="space-y-2">
                {validationResults.metadata_validation.map((result: any, index: number) => (
                  <div
                    key={index}
                    className={`p-3 rounded border-l-4 ${
                      result.status === "PASS"
                        ? "bg-green-50 border-green-400"
                        : result.status === "FAIL"
                        ? "bg-red-50 border-red-400"
                        : "bg-yellow-50 border-yellow-400"
                    }`}
                  >
                    <div className="flex items-center">
                      {result.status === "PASS" ? (
                        <CheckCircleIcon className="w-4 h-4 text-green-600 mr-2" />
                      ) : result.status === "FAIL" ? (
                        <XCircleIcon className="w-4 h-4 text-red-600 mr-2" />
                      ) : (
                        <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600 mr-2" />
                      )}
                      <span className="font-medium text-sm">{result.rule_name}</span>
                    </div>
                    <p className="text-sm text-gray-700 mt-1">{result.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {validationResults.overall_status === "FAIL" && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-red-800 text-sm font-medium">
                ⚠️ Validation failed. Changes will not be saved to the database until all validation errors are resolved.
              </p>
            </div>
          )}
        </div>
      )}

      {showValidateButton && !isValidationPassed && (
        <div className="mt-4 text-left">
          <button
            onClick={handleValidateChanges}
            disabled={isValidating}
            className={`px-4 py-2 text-white text-sm rounded ${
              isValidating
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {isValidating ? "Validating..." : "Validate Changes"}
          </button>
        </div>
      )}
      {showSaveButton && isValidationPassed && (
        <div className="mt-4 text-left">
          <button
            onClick={handleSaveMetadata}
            disabled={isSaving}
            className={`px-4 py-2 text-white text-sm rounded ${
              isSaving
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {isSaving ? "Saving..." : "Save Metadata"}
          </button>
        </div>
      )}
    </div>
  );
};

export default DAPTable;
